/**
 * GradesPage — tutor grades grid screen.
 *
 * Selectors: contract/student → subject → period.
 * Loads evaluations, config, and bulletin for the selected context.
 * Hosts GradesGrid, WeightingConfigForm, and AveragesPanel.
 */

import { useState, useEffect } from 'react';
import apiService from '../../services/apiService';
import GradesGrid from '../../components/grades/GradesGrid';
import WeightingConfigForm from '../../components/grades/WeightingConfigForm';
import AveragesPanel from '../../components/grades/AveragesPanel';
import { useEvaluations } from '../../hooks/useEvaluations';
import { useToast } from '../../contexts/ToastContext';

const GradesPage = () => {
  const { toast } = useToast();

  // Reference data
  const [contracts, setContracts] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [loadingRefs, setLoadingRefs] = useState(true);

  // Selector state
  const [selectedContractId, setSelectedContractId] = useState('');
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [selectedPeriodId, setSelectedPeriodId] = useState('');

  // Derived from selected contract
  const selectedContract = contracts.find((c) => String(c.id) === String(selectedContractId)) ?? null;
  const subjects = selectedContract?.subjects ?? [];
  const studentId = selectedContract?.student?.id ?? null;

  // Load contracts + periods once
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingRefs(true);
      try {
        const [contractRes, periodRes] = await Promise.all([
          apiService.getContracts({ status: 'active' }),
          apiService.getPeriods(),
        ]);
        if (cancelled) return;
        setContracts(contractRes?.data ?? contractRes ?? []);
        setPeriods(periodRes?.data ?? periodRes ?? []);
      } catch (err) {
        if (!cancelled) toast('Erreur lors du chargement des données.', 'error');
      } finally {
        if (!cancelled) setLoadingRefs(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [toast]);

  // Reset downstream selectors when contract changes
  const handleContractChange = (e) => {
    setSelectedContractId(e.target.value);
    setSelectedSubjectId('');
    setSelectedPeriodId('');
  };

  const handleSubjectChange = (e) => {
    setSelectedSubjectId(e.target.value);
    setSelectedPeriodId('');
  };

  const ready =
    Boolean(selectedContractId) &&
    Boolean(selectedSubjectId) &&
    Boolean(selectedPeriodId) &&
    Boolean(studentId);

  const {
    evaluations,
    config,
    bulletin,
    loadingData,
    loadingBulletin,
    loadError,
    saveCell,
    saveConfig,
  } = useEvaluations({
    contractId: selectedContractId || null,
    subjectId: selectedSubjectId || null,
    periodId: selectedPeriodId || null,
    studentId: studentId || null,
  });

  const handleCellSuccess = () => {
    toast('Note enregistrée.', 'success');
  };

  const handleConfigSave = async (data) => {
    const result = await saveConfig(data);
    if (result.ok) {
      toast('Configuration enregistrée.', 'success');
    } else {
      toast(result.userMessage || 'Erreur lors de la sauvegarde.', 'error');
    }
    return result;
  };

  const selectClass =
    'w-full bg-[#F5F5F5] border border-transparent rounded-xl py-2.5 px-3 font-montserrat text-sm text-[#4A4A4A] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#EAF4FB] focus:border-[#17386E] transition-all';

  return (
    <div className="space-y-6 max-w-5xl">
      <h1 className="text-2xl font-playfair font-bold text-[#17386E]">
        Grille de notes
      </h1>

      {/* Selectors */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 space-y-4">
        <h2 className="font-montserrat font-semibold text-[#4A4A4A] text-sm">
          Sélection du contexte
        </h2>

        {loadingRefs ? (
          <p className="text-sm font-montserrat text-gray-400 animate-pulse">
            Chargement des données…
          </p>
        ) : (
          <div className="grid grid-cols-3 gap-4">
            {/* Contract/student selector */}
            <div className="flex flex-col gap-1">
              <label htmlFor="contract-select" className="text-xs font-montserrat font-medium text-[#4A4A4A]">
                Contrat / Élève
              </label>
              <select
                id="contract-select"
                value={selectedContractId}
                onChange={handleContractChange}
                className={selectClass}
              >
                <option value="">— Sélectionner —</option>
                {contracts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.student?.name ?? `Contrat #${c.id}`}
                  </option>
                ))}
              </select>
            </div>

            {/* Subject selector */}
            <div className="flex flex-col gap-1">
              <label htmlFor="subject-select" className="text-xs font-montserrat font-medium text-[#4A4A4A]">
                Matière
              </label>
              <select
                id="subject-select"
                value={selectedSubjectId}
                onChange={handleSubjectChange}
                disabled={!selectedContractId}
                className={selectClass}
              >
                <option value="">— Sélectionner —</option>
                {subjects.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Period selector */}
            <div className="flex flex-col gap-1">
              <label htmlFor="period-select" className="text-xs font-montserrat font-medium text-[#4A4A4A]">
                Période
              </label>
              <select
                id="period-select"
                value={selectedPeriodId}
                onChange={(e) => setSelectedPeriodId(e.target.value)}
                disabled={!selectedSubjectId}
                className={selectClass}
              >
                <option value="">— Sélectionner —</option>
                {periods.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name ?? `Période ${p.number}`}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Main grid area (only when context is selected) */}
      {ready && (
        <div className="grid grid-cols-3 gap-6">
          {/* Grid + config — left 2/3 */}
          <div className="col-span-2 space-y-6">
            {/* Grades grid */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <h2 className="font-montserrat font-semibold text-[#4A4A4A] text-sm mb-4">
                Saisie des notes
              </h2>
              {loadingData ? (
                <p className="text-sm font-montserrat text-gray-400 animate-pulse">
                  Chargement des notes…
                </p>
              ) : loadError ? (
                <p className="text-sm font-montserrat text-red-600">
                  Erreur lors du chargement des notes.
                </p>
              ) : (
                <GradesGrid
                  evaluations={evaluations}
                  config={config}
                  subjectId={selectedSubjectId}
                  periodId={selectedPeriodId}
                  onSaveCell={saveCell}
                  onSuccess={handleCellSuccess}
                />
              )}
            </div>

            {/* Weighting config */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <WeightingConfigForm
                config={config}
                onSave={handleConfigSave}
                isLoading={loadingData}
              />
            </div>
          </div>

          {/* Averages panel — right 1/3 */}
          <div className="col-span-1">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sticky top-4">
              <AveragesPanel
                bulletin={bulletin}
                isLoading={loadingBulletin}
                subjectId={selectedSubjectId}
              />
            </div>
          </div>
        </div>
      )}

      {!ready && !loadingRefs && (
        <div className="flex items-center justify-center h-40 bg-white rounded-2xl shadow-sm border border-gray-100">
          <p className="text-sm font-montserrat text-gray-400">
            Sélectionnez un contrat, une matière et une période pour saisir des notes.
          </p>
        </div>
      )}
    </div>
  );
};

export default GradesPage;
